import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Ingest Bank Transactions from Document Step
 *
 * Reads extracted bank transaction data from a DocumentOCRResultV2,
 * upserts BankTransaction records (status: needs_review) for each
 * transaction, runs AI customer resolution scoped to the rows it just created
 * so the review UI has a suggested customer, then links each row to the AR
 * payment awaiting it. A row only links when exactly one payment matches on
 * exact amount inside the date window; anything else stays in needs_review
 * for a human to hand-pick.
 *
 * The extraction result is produced by extractDocumentContentV2Step using an
 * org-configured SchemaDefinition. This step expects the extracted result to
 * contain a `transactions` array with objects having these convention-based fields:
 *   - amount (number, required) — transaction credit amount
 *   - date (string, YYYY-MM-DD) — transaction date
 *   - merchant_name / payer_name (string) — who sent the payment
 *   - reference_number (string) — check number or transaction reference
 *   - payment_channel (string) — ach, wire, check, etc.
 *   - description (string) — transaction description/memo
 *   - memo (string, optional) — the payer's full memo / addenda / reference
 *     text, copied verbatim (RMR or OBI segments included). Stored on the
 *     receipt as `originMetadata.bankMemo` and passed on in `receipts` for
 *     downstream steps to judge.
 */
export const ingestBankTransactionsFromDocumentStep = defineStepConfig({
  type: "ingest_bank_transactions_from_document",
  name: "Ingest Bank Transactions from Document",
  description:
    "Upsert BankTransaction records from OCR results and resolve customers",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    document: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "The document containing extracted bank transaction data",
    ),
  }),

  outputSchema: z.object({
    ingestion: z.object({
      processed: z.number().describe("Number of bank transactions upserted"),
      skipped: z
        .number()
        .describe("Number of transactions skipped (no amount)"),
      errors: z.array(
        z.object({
          index: z.number(),
          error: z.string(),
        }),
      ),
    }),
    resolution: z.object({
      resolved: z.number().describe("Rows that got an AI customer match"),
      skipped: z.number().describe("Rows skipped (no merchant name available)"),
      errors: z.array(
        z.object({
          bankTransactionId: z.string(),
          error: z.string(),
        }),
      ),
    }),
    receipts: z
      .array(
        z.object({
          bankTransactionId: z
            .string()
            .describe(
              "The receipt holding this row's cash: the row's own, or the bank-side receipt it was folded into",
            ),
          memo: z
            .string()
            .nullable()
            .describe(
              "The row's extracted bank memo, or null when it has none",
            ),
        }),
      )
      .describe("Every receipt this document's rows landed on, with its memo"),
    matching: z.object({
      matched: z
        .number()
        .describe("Receipts linked to an unambiguous open AR payment"),
      unmatched: z
        .number()
        .describe(
          "Receipts left in needs_review (no candidate, or several) for hand-pick",
        ),
      errors: z.array(
        z.object({
          bankTransactionId: z.string(),
          error: z.string(),
        }),
      ),
    }),
  }),

  configSchema: z.object({}),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 2,
      initialInterval: "10 seconds",
      backoffCoefficient: 2,
    },
  },
});
